import React from "react";
import { motion } from "framer-motion";
import { Bot, Workflow, GraduationCap, MessageCircle, ArrowUpRight, Check } from "lucide-react";

const ICONS = [Bot, Workflow, MessageCircle, GraduationCap];
const BG_IMAGES = [
  "https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHdvcmtmbG93JTIwYW5hbHl0aWNzfGVufDB8fHx8MTc4MTQ2NTIyMnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1757466762489-52fea38071ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBuZXR3b3JrJTIwbm9kZXMlMjBkYXJrfGVufDB8fHx8MTc4MTQ2NTIyMXww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1696446702183-be1e1a4ed01b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxBSSUyMGNoYXRib3R8ZW58MHx8fHwxNzgxNDY1MjIyfDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMHByZXNlbnRhdGlvbnxlbnwwfHx8fDE3ODE0NjUyMjF8MA&ixlib=rb-4.1.0&q=85",
];

export function Services({ t, onContactClick }) {
  return (
    <section id="services" className="relative section-pad">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-8 h-px bg-[#00E5FF]" />
              <span className="eyebrow">{t.services.eyebrow}</span>
            </div>
            <h2
              className="text-white font-semibold leading-[1.05] tracking-tight max-w-2xl"
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: "clamp(30px, 4.5vw, 52px)",
              }}
            >
              {t.services.heading}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[i];
            return (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: "easeOut" }}
                data-testid={`service-card-${i}`}
                className="relative group bg-[#1C2331] border border-white/10 rounded-sm overflow-hidden hover-lift"
              >
                {/* faded bg image */}
                <div
                  className="absolute inset-0 opacity-[0.07] group-hover:opacity-[0.13] transition-opacity duration-500 bg-cover bg-center"
                  style={{ backgroundImage: `url('${BG_IMAGES[i]}')` }}
                />
                <div className="relative p-8 flex flex-col h-full">
                  <div className="flex items-center justify-between">
                    <div
                      className="text-[10px] uppercase tracking-[0.24em] text-[#00E5FF] font-bold"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {s.tag}
                    </div>
                    <div className="w-10 h-10 flex items-center justify-center border border-white/10 rounded-sm bg-[#0B132B]/60">
                      <Icon size={18} className="text-[#00E5FF]" />
                    </div>
                  </div>
                  <h3
                    className="mt-8 text-white font-semibold leading-tight"
                    style={{ fontFamily: "'Outfit', sans-serif", fontSize: "26px" }}
                  >
                    {s.title}
                  </h3>
                  <div
                    className="mt-1 text-[#9BA1A6] text-sm"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {s.subtitle}
                  </div>

                  <ul className="mt-8 space-y-3 flex-1">
                    {s.bullets.map((b, j) => (
                      <li
                        key={j}
                        className="flex items-start gap-3 text-[#9BA1A6] text-sm leading-relaxed"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        <Check size={14} className="text-[#00E5FF] mt-1 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`service-cta-${i}`}
                    onClick={onContactClick}
                    className="mt-10 inline-flex items-center gap-2 self-start text-white text-sm font-semibold border-b border-white/30 hover:border-[#00E5FF] hover:text-[#00E5FF] pb-1 transition-all duration-300"
                    style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: "0.04em" }}
                  >
                    {t.services.discuss_cta}
                    <ArrowUpRight size={14} />
                  </button>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
